/**
 * @fileoverview Hover-to-dropdown bridge for mega menu navigation.
 * Works with <dropdown-element> — these components handle hover logic only,
 * delegating open/close to the dropdown's existing window event API.
 *
 * @example
 * // Basic mega menu with two nav items
 * <mega-menu-container close-delay="400">
 *   <nav>
 *     <mega-menu-trigger dropdown-id="mega-shop">
 *       <a href="/collections">Shop</a>
 *     </mega-menu-trigger>
 *     <mega-menu-trigger dropdown-id="mega-about">
 *       <a href="/about">About</a>
 *     </mega-menu-trigger>
 *   </nav>
 *
 *   <dropdown-element id="mega-shop">
 *     <dropdown-panel>
 *       <div>Shop mega menu content</div>
 *     </dropdown-panel>
 *   </dropdown-element>
 *
 *   <dropdown-element id="mega-about">
 *     <dropdown-panel>
 *       <div>About mega menu content</div>
 *     </dropdown-panel>
 *   </dropdown-element>
 * </mega-menu-container>
 *
 * @example
 * // Custom close delay
 * <mega-menu-container close-delay="600">
 *   ...
 * </mega-menu-container>
 */

class MegaMenuContainer extends HTMLElement {
  #activeDropdownId = null;
  #closeTimer = null;

  connectedCallback() {
    this.addEventListener('mouseenter', () => {
      this.#cancelClose();
    });

    this.addEventListener('mouseleave', () => {
      this.#scheduleClose();
    });

    // A dropdown can close itself (outside click, Escape) without going through
    // closeActive(). Without this, #activeDropdownId goes stale and openDropdown()
    // early-returns, so re-hovering the same trigger does nothing. Only null the
    // field — dispatching here would re-enter close() and recurse forever.
    this.addEventListener('dropdown:close', (event) => {
      if (event.target?.id && event.target.id === this.#activeDropdownId) {
        this.#activeDropdownId = null;
      }
    });
  }

  disconnectedCallback() {
    this.#cancelClose();
  }

  /**
   * Opens a dropdown by ID. Closes any other active dropdown instantly.
   * @param {string} id - The dropdown element ID to open
   */
  openDropdown(id) {
    if (this.#activeDropdownId === id) return;

    // Close current dropdown instantly (no delay)
    if (this.#activeDropdownId) {
      window.dispatchEvent(new Event(`close-dropdown-${this.#activeDropdownId}`));
    }

    this.#cancelClose();
    this.#activeDropdownId = id;
    window.dispatchEvent(new Event(`open-dropdown-${id}`));
  }

  /** Closes the active dropdown immediately */
  closeActive() {
    if (!this.#activeDropdownId) return;
    window.dispatchEvent(new Event(`close-dropdown-${this.#activeDropdownId}`));
    this.#activeDropdownId = null;
  }

  #scheduleClose() {
    this.#cancelClose();
    const delay = parseInt(this.getAttribute('close-delay'), 10) || 400;
    this.#closeTimer = setTimeout(() => {
      this.closeActive();
    }, delay);
  }

  #cancelClose() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }
}

class MegaMenuTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('mouseenter', () => {
      const dropdownId = this.getAttribute('dropdown-id');
      if (!dropdownId) return;

      const container = this.closest('mega-menu-container');
      if (container) {
        container.openDropdown(dropdownId);
      }
    });
  }
}

customElements.define('mega-menu-container', MegaMenuContainer);
customElements.define('mega-menu-trigger', MegaMenuTrigger);
